import type { ButtonHTMLAttributes, ReactNode } from 'react';

/**
 * **Le bouton du produit — la seule façon d'agir.**
 *
 * **Quatre variantes, et elles viennent des tokens, pas d'un goût.** `tokens.css`
 * déclare exactement quatre familles d'action : `--action` pleine, `--action-douce-*`,
 * `--action-faible-*`, et rien pour le fantôme, qui n'est qu'un texte. Une cinquième
 * variante serait une valeur redécidée ici, ce que `design-system-first` refuse.
 *
 * **Le violet est la langue de l'interface, et LUI SEUL AGIT.** Aucun bouton ne porte de
 * vert, d'ambre ou de rouge : « le vert, l'ambre et le rouge sont la langue des statuts,
 * et ils ne paraissent que dans une pastille ou sous un bandeau ». Un bouton rouge de
 * suppression n'existe donc pas — c'est le **libellé** qui porte la gravité, et la
 * confirmation qui porte la garde. « Une couleur porte un NIVEAU, un mot porte une
 * IDENTITÉ. »
 *
 * **Aucune cible en dessous de 44 points**, `--cible-tactile`, « le tactile étant ouvert
 * des deux côtés, stylet ou doigt ». Même la petite taille la respecte : elle réduit le
 * texte et le remplissage, jamais la cible.
 *
 * **Aucune marge externe**, anti-pattern 6 : c'est la page qui espace ses blocs.
 */

type Variante = 'action' | 'douce' | 'faible' | 'fantome';
type Taille = 'normale' | 'petite';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  readonly variante?: Variante;
  readonly taille?: Taille;
  /** Bloque le bouton ET annonce l'attente. Un bouton qui attend ne se reclique pas. */
  readonly enCours?: boolean;
  readonly pleineLargeur?: boolean;
  readonly children: ReactNode;
};

const PAR_VARIANTE: Record<Variante, string> = {
  action:
    'bg-action text-action-texte hover:bg-action-survol active:bg-action-actif',
  douce:
    'bg-action-douce-fond text-action-douce-texte hover:bg-bord',
  faible:
    'bg-action-faible-fond text-action-faible-texte hover:bg-fond-selection',
  fantome: 'bg-transparent text-action hover:bg-action-faible-fond',
};

const PAR_TAILLE: Record<Taille, string> = {
  normale: 'text-16 px-4 py-2',
  petite: 'text-14 px-3 py-1',
};

export function Bouton({
  variante = 'action',
  taille = 'normale',
  enCours = false,
  pleineLargeur = false,
  disabled,
  children,
  className,
  ...reste
}: Props) {
  const bloque = disabled === true || enCours;
  return (
    <button
      type="button"
      {...reste}
      disabled={bloque}
      aria-busy={enCours || undefined}
      className={[
        'inline-flex items-center justify-center gap-2',
        'min-h-cible rounded-2 font-texte font-weight-700',
        'transition-colors duration-1',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        PAR_VARIANTE[variante],
        PAR_TAILLE[taille],
        pleineLargeur ? 'w-full' : '',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
